"use client";

import { useRef, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

interface FootnoteTooltipFrameProps {
  number: React.ReactNode;
  triggerClassName: string;
  children: React.ReactNode;
}

/** Preserve Tooltip behavior while allowing focus to move into footnote links. */
export default function FootnoteTooltipFrame({
  number,
  triggerClassName,
  children,
}: FootnoteTooltipFrameProps) {
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <Tooltip tapToOpen open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label={`각주 ${number} 보기`}
          className={triggerClassName}
          onBlur={(event) => {
            if (contentRef.current?.contains(event.relatedTarget)) {
              // Radix normally closes on trigger blur, before a link's click.
              event.preventDefault();
            }
          }}
          onKeyDown={(event) => {
            if (event.key !== "Tab" || event.shiftKey || !open) return;
            const link = contentRef.current?.querySelector<HTMLAnchorElement>("a[href]");
            if (link) {
              event.preventDefault();
              link.focus();
            }
          }}
        >
          [{number}]
        </button>
      </TooltipTrigger>
      <TooltipContent
        ref={contentRef}
        interactive
        side="top"
        className="max-w-80 text-sm leading-relaxed md:!max-w-2xl !w-auto !text-left !text-wrap"
        onEscapeKeyDown={() => {
          if (contentRef.current?.contains(document.activeElement)) {
            triggerRef.current?.focus();
          }
        }}
      >
        <div
          onBlur={(event) => {
            const next = event.relatedTarget;
            if (!event.currentTarget.contains(next) && next !== triggerRef.current) {
              setOpen(false);
            }
          }}
        >
          {children}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
